import React from "react";
import {Button, ClickAwayListener, MenuItem, MenuList, Paper, Popper} from "@mui/material";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import * as Auditor from "js/common/auditor";
import * as Popups from "js/common/popups";

const KpiActions = ({
  kpi,
  isEditor,
  isCube19User,
  clientHasEdit,
  handleKpiChange,
  onRevertToLegacyFormat,
  onDisableMetricToggle,
  onDeleteMetric,
  handleDuplicateClick,
  handleInheritClick,
  inheritAndDuplicateDisabled,
  legacyConfigMessage,
  hasNewConfig,
  variant
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const {theme} = React.useContext(CustomThemeContext);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const fullStyle = {
    background: open ? theme.palette.background.paper : "transparent",
    color: theme.palette.primary.main,
    padding: 5,
    height: 20,
    lineHeight: "20px",
    minWidth: 20,
    flexShrink: "none",
    boxShadow: "none",
    borderRadius: 5,
    marginRight: 0,
    position: "absolute",
    top: 10,
    right: 10
  };

  const kebabStyle = {
    background: open ? theme.palette.background.paper : "transparent",
    padding: 0,
    height: 20,
    lineHeight: "20px",
    minWidth: 20,
    flexShrink: "none",
    boxShadow: "none",
    borderRadius: 5,
    marginRight: 0
  };

  const fullLabel = <><i className="bhi-sort-desc" style={{marginRight: 7}} />ACTIONS</>;
  const kebabLabel = <i className="bhi-kebab-menu" style={{fontSize: 14}} />;

  return (
      <>
        <Button
            aria-controls={open ? "menu-list-grow" : undefined}
            aria-haspopup="true"
            style={variant === "full" ? fullStyle : kebabStyle}
            data-test-id="dropdown-options"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
        >
          {variant === "full" ? fullLabel : kebabLabel}
        </Button>
        <Popper
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            placement="bottom-end"
            style={{zIndex: 999999}}
        >
          <Paper>
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  style={{background: theme.palette.background.card}}
              >
                {((isEditor && isCube19User) || clientHasEdit) && kpi.get("visible") && (
                    <div title={inheritAndDuplicateDisabled ? legacyConfigMessage : "Duplicate"}>
                      <MenuItem
                          data-test-id="duplicate-metric-option"
                          disabled={inheritAndDuplicateDisabled}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateClick(kpi.get("id"));
                            handleClose();
                          }}
                      >
                        <div style={{fontSize: 12}}>
                          <i
                              className="bhi-duplicate"
                              style={{
                                color: theme.themeId === "light" ? "#333333" : "white",
                                marginRight: 10
                              }}
                          />
                          Duplicate Metric
                        </div>
                      </MenuItem>
                    </div>
                )}
                {((isEditor && isCube19User) || clientHasEdit) && kpi.get("visible") && (
                    <div title={inheritAndDuplicateDisabled ? legacyConfigMessage : "Inherit"}>
                      <MenuItem
                          data-test-id="inherit-metric-option"
                          disabled={inheritAndDuplicateDisabled}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInheritClick(kpi.get("id"));
                            handleClose();
                          }}
                      >
                        <div style={{fontSize: 12}}>
                          <i
                              className="far fa-copy"
                              style={{
                                color: theme.themeId === "light" ? "#333333" : "white",
                                marginRight: 12
                              }}
                          />
                          Inherit from this Metric
                        </div>
                      </MenuItem>
                    </div>
                )}
                {isEditor && isCube19User && hasNewConfig && (
                    <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onRevertToLegacyFormat(kpi.get("id"));
                          handleClose();
                        }}
                    >
                      <div style={{fontSize: 12}}>
                        <i
                            className="fa fa-undo"
                            title="Revert"
                            style={{
                              color: theme.themeId === "light" ? "#333333" : "white",
                              fontSize: 10,
                              marginRight: 12
                            }}
                        />
                        Revert to legacy format
                      </div>
                    </MenuItem>
                )}
                <MenuItem
                    data-test-id={`${kpi.get("visible") ? "hide" : "show"}-metric-option`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleKpiChange(kpi.set("visible", !kpi.get("visible")));
                      handleClose();
                      if (kpi.get("visible")) {
                        Popups.success("Metric has been hidden.");
                        Auditor.audit("edit-metrics-admin:hide-metric", {kpiId: kpi.get("id")});
                      } else {
                        Popups.success("Metric has been un-hidden.");
                        Auditor.audit("edit-metrics-admin:show-metric", {kpiId: kpi.get("id")});
                      }
                    }}
                >
                  <div style={{cursor: "pointer", fontSize: 12}}>
                    {kpi.get("visible") ? (
                        <>
                          <i
                              className="fa fa-eye-slash"
                              title="Hide this Metric in-app"
                              style={{marginLeft: -2, marginRight: 5, color: theme.palette.text.main}}
                          />
                          Hide Metric
                        </>
                    ) : (
                        <>
                          <i
                              className="fa fa-eye"
                              title="Show this Metric in-app"
                              style={{marginLeft: -2, marginRight: 5, color: theme.palette.text.main}}
                          />
                          Show Metric
                        </>
                    )}
                  </div>
                </MenuItem>
                {isEditor && isCube19User && (
                    <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onDisableMetricToggle(kpi.get("id"), handleKpiChange);
                          handleClose();
                        }}
                    >
                      <div style={{cursor: "pointer", fontSize: 12}}>
                        {!kpi.get("enabled") ? (
                            <>
                              <i
                                  className="fa fa-check"
                                  title="Enable this Metric in-app"
                                  style={{marginLeft: -2, marginRight: 8, color: theme.palette.text.main}}
                              />
                              Enable Metric
                            </>
                        ) : (
                            <>
                              <i
                                  className="fa fa-times"
                                  title="Disable this Metric in-app"
                                  style={{marginLeft: 2, marginRight: 8, color: theme.palette.text.main}}
                              />
                              Disable Metric
                            </>
                        )}
                      </div>
                    </MenuItem>
                )}
                {isEditor && isCube19User && (
                    <MenuItem
                        data-test-id="delete-metric-option"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteMetric(kpi.get("id"));
                          handleClose();
                        }}
                    >
                      <div style={{cursor: "pointer", fontSize: 12}}>
                        <>
                          <i
                              className="fa fa-trash"
                              title="Delete this Metric"
                              style={{marginLeft: 2, marginRight: 8, color: theme.palette.text.main}}
                          />
                          Delete Metric
                        </>
                      </div>
                    </MenuItem>
                )}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Popper>
      </>
  );
};

export default KpiActions;